import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Cardinal, DrawHistory } from '../types';

const MailForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    email: '',
    cardinal: null as Cardinal | null
  });
  const [selectedCardinal, setSelectedCardinal] = useState<Cardinal | null>(null);
  
  // Récupérer l'historique de tirage et le cardinal sélectionné du localStorage
  useEffect(() => {
    try {
      // Récupérer l'historique de tirage de localStorage
      const storedHistory = localStorage.getItem('drawHistory');
      
      if (storedHistory) {
        const drawHistory: DrawHistory[] = JSON.parse(storedHistory);
        
        // Prendre le cardinal le plus récent s'il y en a un
        if (drawHistory.length > 0) {
          const cardinal = drawHistory[0].cardinal;
          console.log('Cardinal récupéré du localStorage:', cardinal.name);
          setSelectedCardinal(cardinal);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du cardinal depuis localStorage:', error);
    }
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation basique
    if (!formData.firstName || !formData.lastName || !formData.age || !formData.email) {
      setErrorMessage('Tous les champs sont obligatoires.');
      setSubmitStatus('error');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setErrorMessage('Veuillez entrer une adresse email valide.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      let responseData;
      
      // Utiliser le cardinal sélectionné dans le PackOpener
      if (selectedCardinal) {
        console.log('Utilisation du cardinal déjà sélectionné:', selectedCardinal.name);
        
        // Mettre à jour les données du formulaire avec le cardinal sélectionné
        const formDataWithCardinal = {
          ...formData,
          cardinal: selectedCardinal
        };
        
        // Envoyer les données incluant le cardinal
        const response = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formDataWithCardinal),
        });
        
        // Vérifier si la réponse est OK sans lire le corps
        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }
        
        // Lire le corps une seule fois
        responseData = await response.json();
      } else {
        // Aucun cardinal disponible, envoyer sans attribution
        const response = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        
        // Vérifier si la réponse est OK sans lire le corps
        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }
        
        // Lire le corps une seule fois
        responseData = await response.json();
      }

      // Marquer la soumission comme réussie
      setSubmitStatus('success');
      
      // Rediriger vers une page de confirmation
      setTimeout(() => {
        navigate('/merci');
      }, 2000);

      setSubmitStatus('success');

      // Rediriger vers la page d'accueil après 10 secondes
      setTimeout(() => {
        navigate('/');
      }, 10000);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur inattendue est survenue');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 sm:p-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-xl border border-gray-700">
      <motion.h2
        className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-6 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        Recevez votre cardinal par email
      </motion.h2>

      <motion.p
        className="text-gray-300 mb-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        Remplissez ce formulaire pour recevoir votre cardinal et prier pour lui pendant le conclave.
      </motion.p>

      {submitStatus === 'success' ? (
        <motion.div
          className="bg-green-900/50 p-4 rounded-lg text-center mb-6"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <p className="text-green-200 font-bold mb-2">✓ Formulaire envoyé avec succès !</p>
          <p className="text-green-300">
            Vous allez être redirigé vers la page d'accueil dans 10 secondes...
          </p>
        </motion.div>
      ) : (
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {submitStatus === 'error' && (
            <motion.div
              className="bg-red-900/50 p-4 rounded-lg text-center mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <p className="text-red-200">{errorMessage}</p>
            </motion.div>
          )}

          <div className="mb-4">
            <label className="block text-gray-300 mb-2" htmlFor="firstName">
              Prénom
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
              placeholder="Votre prénom"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2" htmlFor="lastName">
              Nom
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
              placeholder="Votre nom"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2" htmlFor="age">
              Âge
            </label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleChange}
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
              placeholder="Votre âge"
              min="1"
              max="120"
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-300 mb-2" htmlFor="email">
              Adresse email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
              placeholder="votre@email.com"
            />
          </div>

          <div className="flex justify-between items-center">
            <Link
              to="/"
              className="px-5 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Retour
            </Link>

            <motion.button
              type="submit"
              className="px-5 py-3 bg-gradient-to-r from-yellow-600 to-yellow-800 text-white font-bold rounded-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Envoi...
                </span>
              ) : (
                'Envoyer'
              )}
            </motion.button>
          </div>
        </motion.form>
      )}
    </div>
  );
};

export default MailForm;
